import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShoppingBag, Zap, Truck, RotateCcw, ShieldCheck, Check } from "lucide-react";
import { STORE_API, useCart, imgUrl, inr, pctOff } from "../store/CartContext";
import { QtyControl } from "../components/store/CartDrawer";
import { WishButton } from "../components/store/ProductCard";
import ProductCard from "../components/store/ProductCard";
import ProductRatings from "../components/store/ProductRatings";
import { StarDisplay } from "../components/store/Stars";

function PincodeCheck() {
  const [pin, setPin] = useState("");
  const [msg, setMsg] = useState("");
  const check = () => setMsg(/^[1-9][0-9]{5}$/.test(pin)
    ? pin.startsWith("75") ? "Delivery in 1–3 days to your area (Odisha)." : "We deliver here! Usually arrives in 4–7 days."
    : "Enter a valid 6-digit pincode.");
  return (
    <div className="rounded-2xl border border-[#3915AA]/10 bg-white p-4">
      <p className="mb-2 flex items-center gap-2 text-sm font-bold"><Truck className="h-4 w-4 text-[#FC5106]" /> Check delivery</p>
      <div className="flex gap-2">
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Pincode" inputMode="numeric" data-testid="pdp-pincode-input" className="w-full rounded-xl border border-[#3915AA]/15 bg-[#F7F5FF] px-3 py-2 text-sm outline-none focus:border-[#FC5106]" />
        <button onClick={check} data-testid="pdp-pincode-check-button" className="rounded-xl bg-[#3915AA] px-4 text-sm font-bold text-white">Check</button>
      </div>
      {msg && <p data-testid="pdp-pincode-result" className="mt-2 text-xs text-[#5E5675]">{msg}</p>}
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [variant, setVariant] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(null);

  useEffect(() => {
    setData(null); setImg(0); setQty(1);
    axios.get(`${STORE_API}/products/${slug}`).then((r) => {
      setData(r.data);
      setVariant(r.data.product.variants.find((v) => v.in_stock) || r.data.product.variants[0]);
      window.gtag?.("event", "view_item", { currency: "INR", items: [{ item_id: r.data.product.id, item_name: r.data.product.name }] });
    }).catch(() => setData({ error: true }));
  }, [slug]);

  if (!data) return <div className="flex min-h-[60vh] items-center justify-center bg-[#F7F5FF]"><Loader2 className="h-6 w-6 animate-spin text-[#FC5106]" /></div>;
  if (data.error) return <div className="min-h-[60vh] bg-[#F7F5FF] py-24 text-center text-[#1E0A5C]"><p className="font-heading text-2xl font-bold">Product not found</p><Link to="/shop" className="mt-3 inline-block font-bold text-[#FC5106]">Back to shop</Link></div>;

  const p = data.product;
  const off = pctOff(variant.price, variant.mrp);
  const images = p.images?.length ? p.images : [null];

  return (
    <div data-testid="product-page" className="bg-[#F7F5FF] text-[#1E0A5C]">
      <section className="container-custom grid gap-8 py-8 lg:grid-cols-2 lg:gap-14 lg:py-12">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="relative aspect-square overflow-hidden rounded-3xl bg-white">
            <img src={imgUrl(images[img])} alt={p.name} data-testid="pdp-main-image" className="h-full w-full object-cover" />
            {off > 0 && <span className="absolute left-4 top-4 rounded-md bg-[#FC5106] px-2.5 py-1 text-xs font-bold text-white">{off}% OFF</span>}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {images.map((u, i) => (
                <button key={u} onClick={() => setImg(i)} data-testid={`pdp-thumb-${i}`} className={`h-20 w-20 overflow-hidden rounded-xl border-2 ${img === i ? "border-[#FC5106]" : "border-transparent"}`}><img src={imgUrl(u)} alt="" className="h-full w-full object-cover" /></button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3915AA]/60"><Link to={`/shop/all?brand=${encodeURIComponent(p.brand)}`} className="hover:text-[#3915AA]">{p.brand}</Link></p>
          <div className="mt-2 flex items-start justify-between gap-3"><h1 data-testid="pdp-title" className="font-heading text-2xl font-bold leading-tight sm:text-4xl">{p.name}</h1><WishButton id={p.id} className="flex-shrink-0 border border-[#3915AA]/15 bg-white" /></div>
          <p className="mt-1 text-sm text-[#5E5675]">For {p.pet_types.join(" & ")}</p>
          {rating?.count > 0 && <a href="#ratings" data-testid="pdp-rating-summary" className="mt-2 inline-block"><StarDisplay value={rating.average} count={rating.count} size="h-4 w-4" /></a>}
          <div className="mt-5 flex items-end gap-3">
            <p data-testid="pdp-price" className="font-heading text-3xl font-black">{inr(variant.price)}</p>
            {off > 0 && <><p className="pb-1 text-base text-gray-400 line-through">MRP {inr(variant.mrp)}</p><p className="pb-1 text-sm font-bold text-green-600">Save {inr(variant.mrp - variant.price)}</p></>}
          </div>
          <p className="text-xs text-[#5E5675]">Inclusive of all taxes</p>

          <p className="mt-6 text-sm font-bold">{p.option_name}: <span className="font-normal">{variant.label}</span></p>
          <div className="mt-2 flex flex-wrap gap-2">
            {p.variants.map((v) => (
              <button key={v.id} onClick={() => { setVariant(v); setQty(1); }} disabled={!v.in_stock} data-testid={`pdp-variant-select-${v.id}`}
                className={`min-w-[84px] rounded-xl border-2 px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${variant.id === v.id ? "border-[#3915AA] bg-white" : "border-[#3915AA]/10 bg-white hover:border-[#3915AA]/40"}`}>
                <span className="block text-sm font-bold">{v.label}</span>
                <span className="block text-xs text-[#5E5675]">{v.in_stock ? inr(v.price) : "Sold out"}</span>
              </button>
            ))}
          </div>
          <p data-testid="pdp-stock" className={`mt-3 text-xs font-semibold ${!variant.in_stock ? "text-red-500" : variant.stock <= 5 ? "text-[#FC5106]" : "text-green-600"}`}>
            {!variant.in_stock ? "Out of stock" : variant.stock <= 5 ? `Hurry, only ${variant.stock} left!` : "In stock"}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <QtyControl value={qty} onChange={setQty} max={variant.stock} testid="pdp-quantity" />
            <button onClick={() => add(p, variant, qty)} disabled={!variant.in_stock} data-testid="pdp-add-to-cart-button" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-[#3915AA] px-6 py-3 text-sm font-bold text-[#3915AA] hover:bg-[#3915AA] hover:text-white disabled:opacity-40 sm:flex-none">
              <ShoppingBag className="h-4 w-4" /> Add to cart
            </button>
            <button onClick={() => { add(p, variant, qty, false); navigate("/checkout"); }} disabled={!variant.in_stock} data-testid="pdp-buy-now-button" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#FC5106] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#E24804] disabled:bg-gray-300 sm:flex-none">
              <Zap className="h-4 w-4" /> Buy now
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 text-center text-[11px] font-semibold">
            {[[Truck, "All-India delivery"], [ShieldCheck, "100% genuine"], [RotateCcw, "Easy support"]].map(([I, t]) => (
              <div key={t} className="rounded-xl bg-white p-3"><I className="mx-auto mb-1 h-4 w-4 text-[#FC5106]" />{t}</div>
            ))}
          </div>
          <div className="mt-4"><PincodeCheck /></div>

          {p.highlights?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {p.highlights.map((h) => <li key={h} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />{h}</li>)}
            </ul>
          )}
          <div className="mt-6 rounded-2xl bg-white p-5">
            <p className="mb-2 font-heading text-lg font-bold">About this product</p>
            <p data-testid="pdp-description" className="whitespace-pre-line text-sm leading-relaxed text-[#5E5675]">{p.description}</p>
          </div>
        </div>
      </section>
      <div id="ratings"><ProductRatings productId={p.id} onChange={setRating} /></div>
      {data.related.length > 0 && <RelatedRail items={data.related} />}
      <div className="pb-10" />
    </div>
  );
}

function RelatedRail({ items }) {
  return (
    <section data-testid="pdp-related" className="container-custom py-8">
      <h2 className="mb-5 font-heading text-2xl font-bold">You may also like</h2>
      <div className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 [scrollbar-width:none]">
        {items.map((p) => <div key={p.id} className="w-[46%] flex-shrink-0 snap-start sm:w-[31%] lg:w-[23%]"><ProductCard product={p} /></div>)}
      </div>
    </section>
  );
}
